/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { EMPTY_STRING } from '../../const';
import {
  closeButton,
  videoContainer,
  videoOverlay,
  videoWrapper,
} from '../FAProgrammes/styles';
import Link from '../Link';
import Text from '../Text';
import useStyles from './styles';

const DisabilityFootball = ({
  heading,
  description,
  descriptionFirstLine,
  descriptionSecondLine,
  descriptionThirdLine,
  descriptionLink,
  images,
  indexVal,
  ctaId,
  videos,
  footer,
  ctaOnClick,
}) => {
  const styles = useStyles();
  const [showVideo, setShowVideo] = useState(false);
  const [showExpandLink, setShowExpandLink] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [linkHovered, setLinkHovered] = useState(false);
  const [linkTouched, setLinkTouched] = useState(false);
  const descriptionRef = useRef(null);

  //converting jsx to string
  const extractTextFromJSX = jsx => {
    if (typeof jsx === 'string') {
      return jsx;
    }
    if (Array.isArray(jsx)) {
      return jsx.map(extractTextFromJSX).join(' ');
    }
    if (jsx?.props?.children) {
      return extractTextFromJSX(jsx.props.children);
    }
    return '';
  };

  const getAnalyticsId = value => {
    const footerText = extractTextFromJSX(value).trim().toLowerCase();
    return footerText
      .replace(/\s+/g, '_');
  };

  const plainTextDescription = extractTextFromJSX(descriptionSecondLine);
  const otherDescription = extractTextFromJSX(description);
  const disabilitySectionFooterAnalyticsId = getAnalyticsId(footer);

  //slicing football and the content
  const footballName = otherDescription.slice(
    0,
    otherDescription.indexOf('Football')
  );
  //for the text that contains show more
  const footballContent = otherDescription.slice(
    footballName?.length + 9,
    otherDescription?.length
  );

  //for the text that contains show less
  const showMoreData = otherDescription.slice(footballName?.length + 9, 152);

  const disableScrolling = () => {
    document.documentElement.style.overflow = 'hidden';
  };

  const enableScrolling = () => {
    document.documentElement.style.overflow = '';
  };

  const handleClick = () => {
    setShowVideo(true);
    disableScrolling();
  };

  const handleCloseVideo = e => {
    e.stopPropagation();
    setShowVideo(false);
    enableScrolling();
  };

  const handleKeyDown = (e, action) => {
    switch (e.key) {
      case 'Enter':
        if (e.key === 'Enter') {
          e.preventDefault();
        }
        if (action === 'linkClick') {
          handleLinkClick(e);
        }
        if (action === 'buttonClick') {
          handleExpandClick(e);
        }
        if (action === 'buttonCollapseClick') {
          handleCollapseClick(e);
        }
        if (action === 'open') {
          handleClick();
        } else if (action === 'close') {
          handleCloseVideo(e);
        }
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (descriptionRef.current) {
      //finding the number of lines by diving the scroolheight and lineheight
      const element = descriptionRef.current;
      const lineHeight = parseFloat(
        window.getComputedStyle(element).lineHeight
      );
      const scrollHeight = element.scrollHeight;
      const lines = Math.ceil(scrollHeight / lineHeight);

      //showmore display state update
      setShowExpandLink(() => {
        const shouldShow =
          lines > 2 && descriptionSecondLine
            ? true
            : lines > 5 && description
            ? true
            : false;
        return shouldShow;
      });
    }
  }, [description, descriptionSecondLine]);

  const handleExpandClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(true);
  };

  const handleCollapseClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(false);
  };

  const handleLinkClick = e => {
    e.stopPropagation();
  };

  return (
    <div css={styles.disablityContainer}>
      <div css={styles.disablityCards} id={`disability-card-${ctaId}`}>
        <div css={styles.innerContainer}>
          <div css={styles.card}>
            <Link
              style={{ cursor: `${videos ? 'pointer': 'auto'}`}}
              css={styles.imgSection(images, indexVal)}
              onClick={videos && handleClick}
              id={ctaId}
              tabIndex={videos && 0}
              onKeyDown={e => handleKeyDown(e, 'open')}
            >
              {videos ? (
                <>
                  {showVideo && (
                    <div
                      css={videoOverlay}
                      onClick={handleCloseVideo}
                      onKeyDown={e => handleKeyDown(e, 'close')}
                      tabIndex={0}
                      role="button"
                    >
                      <div
                        css={videoContainer}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        role="button"
                      >
                        <div css={videoWrapper}>
                          <iframe
                            width="100%"
                            height="100%"
                            src={videos}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="video"
                          ></iframe>
                          <span
                            onClick={handleCloseVideo}
                            role="button"
                            tabIndex="0"
                            onKeyDown={e => handleKeyDown(e, 'close')}
                          >
                            <img
                              css={closeButton}
                              src="/images/desktop/home/closeLightboxVideo.svg"
                              alt="Close Video"
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!showVideo && (
                    <div css={styles.imageWrapper}>
                      <span
                        onClick={handleClick}
                        className="youtube"
                        role="button"
                        onKeyDown={e => handleKeyDown(e, 'open')}
                      >
                        <img
                          src="/images/desktop/home/disabilityFootball/videoPlay.svg"
                          alt="Play Video"
                        />
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <></>
              )}
            </Link>

            <div css={styles.infoSection}>
              <div css={styles.cardalign(isExpanded)}>
                <Text
                  as="p"
                  css={styles.header}
                  onClick={videos && handleClick}
                  role="link"
                  onKeyDown={e => handleKeyDown(e, 'open')}
                  style={{ cursor: `${videos ? 'pointer': 'auto'}`}}
                >
                  {heading}
                </Text>
                <Text
                  css={styles.description(isExpanded)}
                  ref={descriptionRef}
                  onClick={videos && handleClick}
                  role="link"
                  id={`disability-section-${ctaId}`}
                  onKeyDown={e => handleKeyDown(e, 'open')}
                  style={{ cursor: `${videos ? 'pointer': 'auto'}`}}
                >
                  <span> {descriptionFirstLine}</span>
                  <div>
                    {showExpandLink && (
                      <div>
                        {!isExpanded ? (
                          <span>
                            {descriptionSecondLine ? (
                              plainTextDescription.slice(0, 50)
                            ) : (
                              <span css={styles.boldData}>
                                {' '}
                                {footballName + 'Football'}
                              </span>
                            )}
                            {!descriptionSecondLine && showMoreData}...{' '}
                          </span>
                        ) : (
                          <span>
                            {descriptionSecondLine}
                            {descriptionThirdLine}
                            {description ? description : EMPTY_STRING}
                            <span
                              css={styles.descriptionLink}
                              onClick={handleLinkClick}
                              onKeyDown={e => handleKeyDown(e, 'linkClick')}
                              tabIndex={0}
                              role="button"
                            >
                              {descriptionLink}
                            </span>
                          </span>
                        )}
                        <span
                          onClick={
                            !isExpanded
                              ? handleExpandClick
                              : handleCollapseClick
                          }
                          tabIndex={0}
                          role="button"
                          css={styles.showMore}
                          onKeyDown={e =>
                            !isExpanded
                              ? handleKeyDown(e, 'buttonClick')
                              : handleKeyDown(e, 'buttonCollapseClick')
                          }
                        >
                          {!isExpanded ? 'show more' : 'show less'}
                          <span css={styles.expandArrow}>
                            <img
                              src={
                                !isExpanded
                                  ? '/images/desktop/home/disabilityFootball/downArrow.svg'
                                  : '/images/desktop/home/disabilityFootball/upArrow.svg'
                              }
                              alt="/images/desktop/home/disabilityFootball/upArrow.svg"
                            />
                          </span>
                        </span>
                      </div>
                    )}
                  </div>

                  {!showExpandLink && (
                    <>
                      <span css={styles.boldData}>
                        {' '}
                        {footballName + 'Football'}
                      </span>
                      {footballContent}{' '}
                    </>
                  )}
                </Text>

                <Text
                  css={styles.learnMore}
                  id={disabilitySectionFooterAnalyticsId}
                  tabIndex={0}
                  onMouseEnter={() => setLinkHovered(true)}
                  onMouseLeave={() => setLinkHovered(false)}
                  onTouchStart={() => setLinkTouched(true)}
                  onTouchEnd={() => setLinkTouched(false)}
                  onClick={() => ctaOnClick()}
                  onKeyDown={(e) => e.key === 'Enter' && ctaOnClick()}
                >
                  <span css={styles.learnmore} >
                    {footer}
                  </span>
                  <img
                    css={styles.learnmoreArrow}
                    src={
                      linkHovered || linkTouched
                        ? '/images/desktop/home/disabilityFootball/rightArrowBlue.svg'
                        : '/images/desktop/home/disabilityFootball/rightArrow.svg'
                    }
                    alt="/images/desktop/home/rightArrow.svg"
                  />{' '}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DisabilityFootball.propTypes = {
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  descriptionFirstLine: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  descriptionSecondLine: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  descriptionThirdLine: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  descriptionLink: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  images: PropTypes.object,
  indexVal: PropTypes.any,
  ctaId: PropTypes.string,
  videos: PropTypes.any,
  footer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default DisabilityFootball;
